import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useAddPromptMutation,
  useDeletePromptMutation,
  useGetPromptQuery,
  useUpdatePromptMutation,
  useGetOrganizationStylesQuery,
} from "apis/prompt";
import { FaPlus } from "react-icons/fa6";
import PromptEdit from "components/prompt/PromptEdit";
import PromptView from "components/prompt/PromptView";
import { API_ROUTES } from "apis/routes";
import Table from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";
import {
  ANY_ORG_FILTER_OPTION,
  ANY_STYLE_FILTER_OPTION,
  NO_ORG_FILTER_OPTION,
  NO_STYLE_FILTER_OPTION,
  PromptScopeFilter,
  isPromptScopeAny,
  isPromptScopeNone,
  isSpecificPromptScopeId,
} from "constants/promptScopeFilter";

const Prompts = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [orgFilter, setOrgFilter] = useState(PromptScopeFilter.Any);
  const [styleFilter, setStyleFilter] = useState(PromptScopeFilter.Any);
  const pageSize = 10;

  const { mutate: updatePrompt } = useUpdatePromptMutation();
  const { mutate: addPrompt, isLoading: isCreateLoading } =
    useAddPromptMutation();
  const { mutate: deletePrompt } = useDeletePromptMutation();

  const { data: organizationStyles } = useGetOrganizationStylesQuery();

  const queryParams = useMemo(
    () => ({
      organizationStyleId: orgFilter,
      styleId: styleFilter,
    }),
    [orgFilter, styleFilter]
  );

  const {
    data,
    isPending: isPromptsPending,
    isError: isPromptsError,
    error: PromptsError,
  } = useGetPromptQuery(queryParams);

  const selectedOrg = (organizationStyles || []).find(
    (o) => o._id === orgFilter
  );

  const orgFilterOptions = [
    ANY_ORG_FILTER_OPTION,
    NO_ORG_FILTER_OPTION,
    ...(organizationStyles || []).map((o) => ({
      value: o._id,
      label: o.name,
    })),
  ];

  const styleFilterOptions = [
    ANY_STYLE_FILTER_OPTION,
    NO_STYLE_FILTER_OPTION,
    ...(selectedOrg?.styles || []).map((s) => ({
      value: s._id,
      label: s.name,
    })),
  ];

  const orgFilterValue =
    orgFilterOptions.find((o) => o.value === orgFilter) || ANY_ORG_FILTER_OPTION;
  const styleFilterValue =
    styleFilterOptions.find((s) => s.value === styleFilter) ||
    ANY_STYLE_FILTER_OPTION;

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
      },
      {
        accessorKey: "organizationName",
        header: "Organization",
        isFilter: true,
        cell: (row) => (
          <span className="text-sm">{row.organizationName || "—"}</span>
        ),
      },
      {
        accessorKey: "styleName",
        header: "Style",
        isFilter: true,
        cell: (row) => (
          <span className="text-sm">{row.styleName || "—"}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: (row) => (
          <span className="text-sm">
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Prompt"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                deletePrompt(row._id);
              }}
              className="delete-icon"
              title="Delete Prompt"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [deletePrompt]
  );

  if (!data || isPromptsPending) {
    return <PageLoader />;
  }

  if (isPromptsError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading prompts: {PromptsError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedPrompt = data.find((prompt) => prompt._id === selectedId);

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddPromptButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={16} className="mr-1" />
      Add Prompt
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Prompt Edit"
        className="max-w-2xl"
      >
        <PromptEdit
          initialName={selectedPrompt?.name}
          initialDescription={selectedPrompt?.description}
          initialOrganizationStyleId={selectedPrompt?.organizationStyleId}
          initialStyleId={selectedPrompt?.styleId}
          organizationStyles={organizationStyles}
          onSubmit={(formData) => {
            updatePrompt({ id: selectedId, ...formData });
            setIsEditOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Prompt Add"
        className="max-w-2xl"
      >
        <PromptEdit
          initialName=""
          initialDescription=""
          initialOrganizationStyleId={
            isSpecificPromptScopeId(orgFilter) ? orgFilter : ""
          }
          initialStyleId={
            isSpecificPromptScopeId(styleFilter) ? styleFilter : ""
          }
          organizationStyles={organizationStyles}
          onSubmit={(formData) => {
            addPrompt(formData);
            setIsAddOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Prompt Details"
        className="max-w-2xl"
      >
        <PromptView
          promptId={selectedPrompt?._id}
          versionsRoute={API_ROUTES.GET_PROMPT}
          name={selectedPrompt?.name}
          description={selectedPrompt?.description}
          organizationName={selectedPrompt?.organizationName}
          styleName={selectedPrompt?.styleName}
        />
      </Modal>

      <div className="container mx-auto">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex flex-col w-64">
            <label className="text-xs font-semibold uppercase tracking-wider text-darkergray mb-1">
              Organization
            </label>
            <SearchSelect
              options={orgFilterOptions}
              value={orgFilterValue}
              onChange={(opt) => {
                setOrgFilter(opt?.value ?? PromptScopeFilter.Any);
                setStyleFilter(PromptScopeFilter.Any);
              }}
              placeholder="All organizations"
            />
          </div>

          <div className="flex flex-col w-64">
            <label className="text-xs font-semibold uppercase tracking-wider text-darkergray mb-1">
              Style
            </label>
            <SearchSelect
              options={styleFilterOptions}
              value={styleFilterValue}
              onChange={(opt) => setStyleFilter(opt?.value ?? PromptScopeFilter.Any)}
              disabled={isPromptScopeAny(orgFilter) || isPromptScopeNone(orgFilter)}
              placeholder="All styles"
            />
          </div>

          {(!isPromptScopeAny(orgFilter) || !isPromptScopeAny(styleFilter)) && (
            <button
              type="button"
              onClick={() => {
                setOrgFilter(PromptScopeFilter.Any);
                setStyleFilter(PromptScopeFilter.Any);
              }}
              className="px-3 py-2 text-sm text-darkergray hover:text-black underline"
            >
              Clear filters
            </button>
          )}
        </div>

        <Table
          title="Prompts"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddPromptButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Prompts;
